import React from 'react';
import PropTypes from 'prop-types';
import { Link, useNavigate } from 'react-router-dom';
import { getConfig } from '@edx/frontend-platform';
import { useIntl } from '@edx/frontend-platform/i18n';
import {
  Badge, Button, Icon, IconButton,
} from '@openedx/paragon';
// eslint-disable-next-line import/no-extraneous-dependencies
import {
  RiArrowLeftSLine, RiBookOpenLine, RiBroadcastLine, RiCheckLine, RiFeedbackLine,
  RiFileList2Line, RiLock2Line, RiPlayMiniLine, RiStackLine, RiSurveyLine,
  RiTextSnippet, RiUserVoiceLine, RiVideoLine,
} from '@remixicon/react';

import messages from './messages';

const FALLBACK_IMAGE = 'https://images.unsplash.com/photo-1515879218367-8466d910aaa4?auto=format&fit=crop&w=1200&q=60';

const TYPE_ICON = {
  course: RiBookOpenLine,
  video: RiVideoLine,
  assessment: RiSurveyLine,
  text: RiTextSnippet,
  material: RiFileList2Line,
  live_session: RiBroadcastLine,
  feedback: RiFeedbackLine,
  role_play: RiUserVoiceLine,
};

const resolveImage = (path) => {
  if (!path) { return FALLBACK_IMAGE; }
  return path.startsWith('http') ? path : `${getConfig().LMS_BASE_URL}${path}`;
};

// Course components open the LMS courseware home (external). Non-course
// components (competency library blocks) open inside this MFE in an iframe.
const isCourse = (component) => component.componentType === 'course';
const courseHomeUrl = (component) => `${getConfig().LMS_BASE_URL}/courses/${component.contentId}/home`;
const inMfePath = (pathwayId, component) => `/pathways/${encodeURIComponent(pathwayId)}/component/${component.id}`;

const StatusBadge = ({ component }) => {
  const intl = useIntl();
  if (component.isCompleted) {
    return <Badge variant="soft-success">{intl.formatMessage(messages.completed)}</Badge>;
  }
  if (component.status === 'in_progress') {
    return <Badge variant="soft-info">{intl.formatMessage(messages.inProgress)}</Badge>;
  }
  if (component.isLocked) {
    return <Badge variant="soft-primary">{intl.formatMessage(messages.locked)}</Badge>;
  }
  if (!component.isMandatory) {
    return <Badge variant="soft-primary">{intl.formatMessage(messages.optional)}</Badge>;
  }
  return null;
};
StatusBadge.propTypes = {
  component: PropTypes.shape({
    isCompleted: PropTypes.bool,
    isLocked: PropTypes.bool,
    isMandatory: PropTypes.bool,
    status: PropTypes.string,
  }).isRequired,
};

const ComponentTile = ({ component }) => {
  if (component.isCompleted) {
    return (
      <span className="pathway-inner__component-tile pathway-inner__component-tile--completed">
        <Icon src={RiCheckLine} />
      </span>
    );
  }
  if (component.isLocked) {
    return (
      <span className="pathway-inner__component-tile pathway-inner__component-tile--locked">
        <Icon src={RiLock2Line} />
      </span>
    );
  }
  if (component.imageUrl) {
    return (
      <span
        className="pathway-inner__component-tile pathway-inner__component-tile--image"
        style={{ backgroundImage: `url(${resolveImage(component.imageUrl)})` }}
      />
    );
  }
  return (
    <span className="pathway-inner__component-tile">
      <Icon src={TYPE_ICON[component.componentType] || RiStackLine} />
    </span>
  );
};
ComponentTile.propTypes = {
  component: PropTypes.shape({
    componentType: PropTypes.string,
    imageUrl: PropTypes.string,
    isCompleted: PropTypes.bool,
    isLocked: PropTypes.bool,
  }).isRequired,
};

const PathwayView = ({
  pathway, components, progressPercent, completedCount, resumeComponent,
}) => {
  const intl = useIntl();
  const navigate = useNavigate();
  const total = components.length;
  const remaining = total - completedCount;
  const clamped = Math.max(0, Math.min(100, progressPercent));

  const launchProps = (component) => {
    if (!component || component.isLocked || !component.contentId) { return null; }
    return isCourse(component)
      ? { as: 'a', href: courseHomeUrl(component) }
      : { as: Link, to: inMfePath(pathway.id, component) };
  };
  const resumeProps = launchProps(resumeComponent);

  return (
    <div className="pathway-inner">
      <div
        className="pathway-inner__banner"
        style={{ backgroundImage: `url(${resolveImage(pathway.image)})` }}
        role="img"
        aria-label={pathway.name}
      >
        <div className="pathway-inner__banner-overlay" aria-hidden />
        <IconButton
          src={RiArrowLeftSLine}
          alt={intl.formatMessage(messages.backToLearning)}
          onClick={() => navigate('/courses')}
          variant="inverse-tertiary"
          className="pathway-inner__banner-back"
        />
        <div className="pathway-inner__banner-content">
          <h1 className="pathway-inner__banner-title">{pathway.name}</h1>
          {remaining > 0 && (
            <span className="pathway-inner__banner-meta">
              {intl.formatMessage(messages.componentsLeft, { count: remaining })}
            </span>
          )}
        </div>
      </div>

      <section className="pathway-inner__card">
        <div className="pathway-inner__progress-header">
          <h2 className="pathway-inner__card-title">{intl.formatMessage(messages.progressTitle)}</h2>
          <Button
            variant="primary"
            iconBefore={RiPlayMiniLine}
            disabled={!resumeProps}
            {...(resumeProps || {})}
          >
            {intl.formatMessage(messages.continueLearning)}
          </Button>
        </div>
        <div className="pathway-inner__progress-body">
          <div className="pathway-inner__progress-labels">
            <span>{intl.formatMessage(messages.completedCount, { completed: completedCount, total })}</span>
            <span>{clamped}%</span>
          </div>
          <div
            className="pathway-inner__progress-track"
            role="progressbar"
            aria-label={intl.formatMessage(messages.progressTitle)}
            aria-valuenow={clamped}
            aria-valuemin={0}
            aria-valuemax={100}
          >
            <div className="pathway-inner__progress-fill" style={{ width: `${clamped}%` }} />
          </div>
        </div>
      </section>

      <section className="pathway-inner__card pathway-inner__components">
        <h2 className="pathway-inner__card-title">{intl.formatMessage(messages.componentsTitle)}</h2>
        {!total && (
          <p className="text-muted mb-0">{intl.formatMessage(messages.emptyCurriculum)}</p>
        )}
        <div className="pathway-inner__components-list">
          {components.map((component, index) => {
            const lp = launchProps(component);
            const disabled = !lp;
            const RowTag = lp ? lp.as : 'div';
            let rowProps = {};
            if (lp) { rowProps = lp.href ? { href: lp.href } : { to: lp.to }; }
            return (
              <RowTag
                key={component.id}
                className={`pathway-inner__component-row${component.isLocked ? ' pathway-inner__component-row--locked' : ''}`}
                title={component.isLocked ? intl.formatMessage(messages.lockedHint) : undefined}
                {...rowProps}
              >
                <span className="pathway-inner__component-index">{index + 1}</span>
                <ComponentTile component={component} />
                <div className="pathway-inner__component-info">
                  <span className="pathway-inner__component-title">
                    {component.displayName || component.contentId}
                  </span>
                  <div className="pathway-inner__component-meta">
                    <StatusBadge component={component} />
                  </div>
                </div>
                {!disabled && (
                  <Button variant="tertiary" size="sm" className="pathway-inner__component-action" as="span">
                    {intl.formatMessage(messages.open)}
                  </Button>
                )}
              </RowTag>
            );
          })}
        </div>
      </section>
    </div>
  );
};

PathwayView.propTypes = {
  pathway: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    image: PropTypes.string,
  }).isRequired,
  components: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    contentId: PropTypes.string,
    componentType: PropTypes.string,
    displayName: PropTypes.string,
    imageUrl: PropTypes.string,
    isMandatory: PropTypes.bool,
    isCompleted: PropTypes.bool,
    isLocked: PropTypes.bool,
    status: PropTypes.string,
  })).isRequired,
  progressPercent: PropTypes.number.isRequired,
  completedCount: PropTypes.number,
  resumeComponent: PropTypes.shape({ id: PropTypes.string }),
};

PathwayView.defaultProps = {
  completedCount: 0,
  resumeComponent: null,
};

export default PathwayView;
